/**
 * Display formatting for dates and numbers, in the current language.
 *
 * Every date or number the app SHOWS goes through here, so a second language
 * changes month names and decimal separators in one place. Storage, API payloads,
 * CSV export and input `value` attributes are not display: they keep ISO dates
 * and `.` decimals and do not use this module.
 *
 * Date rules (which tokens, the vet-sheet exception) live in `lib/date.ts` and
 * DECISIONS.md §DS-3; this module only supplies the language.
 */
import { format as formatWithDateFns } from 'date-fns';
import { enUS, es, ptBR } from 'date-fns/locale';
import type { Locale } from 'date-fns';
import i18n, { DEFAULT_LANGUAGE } from '../i18n';

const DATE_LOCALES: Record<string, Locale> = {
  en: enUS,
  'pt-BR': ptBR,
  es,
};

/** The language the UI is currently rendering in. */
export function currentLanguage(): string {
  return i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LANGUAGE;
}

/** The date-fns locale for a language; English when the language has none. */
export function dateLocaleFor(lang: string = currentLanguage()): Locale {
  return DATE_LOCALES[lang] ?? DATE_LOCALES[lang.split('-')[0]] ?? enUS;
}

/** Format a Date with date-fns tokens, in the given (default: current) language. */
export function formatDateTokens(
  date: Date | number,
  tokens: string,
  lang: string = currentLanguage(),
): string {
  return formatWithDateFns(date, tokens, { locale: dateLocaleFor(lang) });
}

const decimalSeparators = new Map<string, string>();

/** The language's decimal separator, from Intl ("." in English, "," in Portuguese and Spanish). */
function decimalSeparator(lang: string): string {
  let sep = decimalSeparators.get(lang);
  if (sep === undefined) {
    sep =
      new Intl.NumberFormat(lang).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? '.';
    decimalSeparators.set(lang, sep);
  }
  return sep;
}

/**
 * Format a number for display.
 *
 *  - `formatNumber(x, n)` shows exactly n decimal places — the display form of
 *    `x.toFixed(n)`, and identical to it in English.
 *  - `formatNumber(x)` shows the number as written — the display form of
 *    `String(x)`, and identical to it in English.
 *
 * Rounding stays with toFixed, which rounds the exact binary value (1.005 → "1.00").
 * Intl.NumberFormat rounds the shortest decimal form instead (1.005 → "1.01"), so
 * using it for the digits would change numbers the app already shows. Intl supplies
 * only the separator. No digit grouping: the app has always shown "1250".
 */
export function formatNumber(
  value: number,
  fractionDigits?: number,
  lang: string = currentLanguage(),
): string {
  const s = fractionDigits === undefined ? String(value) : value.toFixed(fractionDigits);
  const sep = decimalSeparator(lang);
  return sep === '.' ? s : s.replace('.', sep);
}
